import { RefObject, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setHoveredElementKey,
  setSelectedElementKey,
  setMultiSelectWithElementKey,
} from "@/store/iframeSlice";
import {
  useCanvasStore,
  useHoveredNavTreeNode,
  useMultiselectedNavTreeNode,
  useSelectedNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import { TempoElement } from "@/utils/tempoElement";

import {
  findElementByDomUniquePath,
  getCodebaseIdFromNode,
  getNavNodeForDomElement,
} from "../../../utils/utils";
import { NavTreeNode } from "@/utils/types";
import { RootState } from "@/store/store";
import _ from "lodash";
import { useCanvasContextMenu } from "@/components/Canvas/hooks/useCanvasContextMenu";
import { useTextEdit } from "./useTextEdit";
import { CursorType } from "@/store/globalSettingsSlice";
import { useDrawDivInStoryboard } from "./useDrawDivInStoryboard";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { OutlineSystemRef } from "../../OutlineSystem";
import { useAutoUpdateRef } from "@/utils/utils";
import { isNavTreeEmpty } from "../utils/navTreeLookupUtils";
import { useDragElement } from "./useDragElement";
import { SimpleCanvasRef } from "@/components/SimpleCanvas/SimpleCanvas";
import { useExtractElementStore } from "@/components/Canvas/stores/extractElementStore";
import {
  calculateAndSetDropPosition,
  getFirstPositionedParent,
} from "@/components/Canvas/hooks/useCalculateDropPosition";
import { FORCE_FULL_WIDTH_HEIGHT_CLASS_ID } from "../utils/domUtils";

const getNavNodeToSelect = (
  target: HTMLElement,
  storyboardId: string,
  currentSelectedNavNode: NavTreeNode | null,
  isSelectedTopLevelStoryboard: boolean,
  depthFirst: boolean,
): {
  navNode: NavTreeNode;
  domElement: HTMLElement | null;
} | null => {
  if (!storyboardId) {
    return null;
  }

  let searchNode = target;
  while (searchNode) {
    // TODO: This is inefficient, you only need to calculate once then go up the tree & adjust
    // Otherwise this is O(n^2) for no reason
    const navNode = getNavNodeForDomElement(searchNode, storyboardId);

    if (navNode) {
      if (depthFirst) {
        return { navNode, domElement: searchNode };
      }

      if (currentSelectedNavNode) {
        // If there is a selected nav node, only allow selecting:
        // 1) A child of the selected nav node
        // 2) A sibling of the selected nav node
        // 3) Any parent/grandparent/etc of the selected nav node
        // 4) A sibling of any parent/grandparent/etc of the selected nav node
        const uniquePathOfSelectedNavNode =
          currentSelectedNavNode.tempoElement.uniquePath;
        const uniquePathOfClickedNode = navNode.tempoElement.uniquePath;

        // Find the longest common prefix of the unique paths
        const longestCommonPrefix = uniquePathOfSelectedNavNode
          .split("-")
          .filter((_, index) => {
            return (
              uniquePathOfSelectedNavNode.split("-")[index] ===
              uniquePathOfClickedNode.split("-")[index]
            );
          })
          .join("-");

        // Now get the parent that matches this longest common prefix
        let mutualParent: NavTreeNode | undefined = navNode;
        let navNodeToReturn: NavTreeNode | undefined = navNode;
        while (
          mutualParent?.parent &&
          mutualParent?.tempoElement.uniquePath !== longestCommonPrefix
        ) {
          navNodeToReturn = mutualParent;
          mutualParent = mutualParent.parent;
        }

        if (!mutualParent) {
          return null;
        }

        return {
          navNode: navNodeToReturn,
          domElement: findElementByDomUniquePath(
            target.ownerDocument,
            navNodeToReturn.domUniquePath ??
              navNodeToReturn.domUniquePathForComponent,
          ),
        };
      } else if (isSelectedTopLevelStoryboard) {
        // Always pick the top level node in the storyboard
        let toReturn: NavTreeNode | undefined = navNode;
        while (toReturn?.parent) {
          toReturn = toReturn.parent;
        }

        return {
          navNode: toReturn,
          domElement: toReturn
            ? findElementByDomUniquePath(
                target.ownerDocument,
                toReturn.domUniquePath ?? toReturn.domUniquePathForComponent,
              )
            : null,
        };
      }

      return { navNode, domElement: searchNode };
    }

    searchNode = searchNode.parentElement as HTMLElement;
    if (!searchNode || searchNode.tagName?.toLowerCase() === "body") {
      break;
    }
  }
  return null;
};

interface UseElementSelectHandlerProps {
  storyboardId: string;
  allowInteractions: boolean;
  forceDragOver: boolean;
  outlineSystemRef: React.RefObject<OutlineSystemRef>;
  simpleCanvasRef?: React.RefObject<SimpleCanvasRef>;
  isResizing: boolean;
}

export const useElementSelectHandler = ({
  storyboardId,
  allowInteractions,
  forceDragOver,
  outlineSystemRef,
  simpleCanvasRef,
  isResizing,
}: UseElementSelectHandlerProps) => {
  const dispatch = useDispatch();

  const iframeDocRef = useRef<Document | null>(null);
  const {
    canEditText,
    startTextEdit,
    handleBlur,
    handleKeyDown,
    updateElementKeyBeingEdited,
    domUniquePathEditing: domUniquePathTextEditing,
  } = useTextEdit(storyboardId, iframeDocRef);

  const {
    createTextDiv,
    startDrawDiv,
    updateDivSize,
    finishDrawDiv,
    isDrawingRef,
  } = useDrawDivInStoryboard(iframeDocRef, storyboardId, outlineSystemRef);

  const isResizingRef = useAutoUpdateRef(isResizing);

  const updateDivSizeRef = useAutoUpdateRef(updateDivSize);

  const { selectedNavTreeNode, selectedElementKey, storyboardIdSelected } =
    useSelectedNavTreeNode();
  const selectedNavNodeRef = useAutoUpdateRef<NavTreeNode | null>(
    selectedNavTreeNode,
  );
  const isSelectedTopLevelStoryboardRef = useAutoUpdateRef(
    storyboardIdSelected === storyboardId,
  );
  const storyboardIdRef = useAutoUpdateRef(storyboardId);
  const allowInteractionsRef = useAutoUpdateRef(allowInteractions);

  const forceDragOverRef = useAutoUpdateRef(forceDragOver);

  const { hoveredNavTreeNode, hoveredElementKey } = useHoveredNavTreeNode();
  const hoveredNavTreeNodeRef = useAutoUpdateRef(hoveredNavTreeNode);
  const hoveredElementKeyRef = useAutoUpdateRef(hoveredElementKey);

  const selectedElementKeyRef = useAutoUpdateRef(selectedElementKey);

  const cursorType = useSelector(
    (state: RootState) => state.globalSettings.cursorType,
  );
  const cursorTypeRef = useAutoUpdateRef(cursorType);

  const { multiselectedNavTreeNodes } = useMultiselectedNavTreeNode();
  const multiselectedNavTreeNodesRef = useAutoUpdateRef(
    multiselectedNavTreeNodes,
  );

  const domUniquePathTextEditingRef = useAutoUpdateRef(
    domUniquePathTextEditing,
  );
  const handleBlurRef = useAutoUpdateRef(handleBlur);
  const handleKeyDownRef = useAutoUpdateRef(handleKeyDown);

  // Use the new drag element hook
  const {
    isDragging,
    isDraggingAbsolute,
    onMouseDown: dragOnMouseDown,
    onMouseMove: dragOnMouseMove,
    onMouseUp: dragOnMouseUp,
  } = useDragElement({
    storyboardId,
    simpleCanvasRef,
    iframeDocRef,
    outlineSystemRef,
  });

  const isDraggingRef = useAutoUpdateRef(isDragging);

  const selectedElementKeyOnMouseUpRef = useRef<string | null>(null);

  // If the selected element or multiselected element changes, find the html for them
  useEffect(() => {
    if (!iframeDocRef.current) {
      return;
    }

    const nodesToCheck = (
      multiselectedNavTreeNodes?.length
        ? multiselectedNavTreeNodes
        : [selectedNavTreeNode]
    ).filter(Boolean);

    if (!nodesToCheck.length) {
      useExtractElementStore.getState().setSelectedElementSnapshot("", null);
      return;
    }

    const allStoryboardIds = new Set(
      nodesToCheck.map((node) => node?.tempoElement.storyboardId),
    );

    // Wrong storyboard, don't do anything
    if (!allStoryboardIds.has(storyboardId)) {
      return;
    }

    // More than one storyboard, can't combine
    if (allStoryboardIds.size > 1) {
      useExtractElementStore.getState().setSelectedElementSnapshot("", null);
      return;
    }

    const totalBoundingBox = {
      x: Infinity,
      y: Infinity,
      relativeX: 0,
      relativeY: 0,
      width: 0,
      height: 0,
    };

    const htmls = nodesToCheck.map((node) => {
      const elementToUse = findElementByDomUniquePath(
        iframeDocRef.current as Document,
        node?.domUniquePath ?? node?.domUniquePathForComponent,
      );
      const boundingBox = elementToUse?.getBoundingClientRect();

      if (boundingBox && elementToUse && iframeDocRef.current) {
        totalBoundingBox.x = Math.min(totalBoundingBox.x, boundingBox.x);
        totalBoundingBox.y = Math.min(totalBoundingBox.y, boundingBox.y);

        const prevRight = totalBoundingBox.x + totalBoundingBox.width;
        const newRight = boundingBox.x + boundingBox.width;
        totalBoundingBox.width =
          Math.max(prevRight, newRight) - totalBoundingBox.x;

        const prevBottom = totalBoundingBox.y + totalBoundingBox.height;
        const newBottom = boundingBox.y + boundingBox.height;
        totalBoundingBox.height =
          Math.max(prevBottom, newBottom) - totalBoundingBox.y;

        const firstPositionedParent = getFirstPositionedParent(
          iframeDocRef.current.defaultView ?? undefined,
          elementToUse.parentElement ?? undefined,
        );
        if (firstPositionedParent) {
          const firstPositionedParentBoundingBox =
            firstPositionedParent.getBoundingClientRect();
          totalBoundingBox.relativeX =
            boundingBox.x - firstPositionedParentBoundingBox.x;
          totalBoundingBox.relativeY =
            boundingBox.y - firstPositionedParentBoundingBox.y;
        } else {
          totalBoundingBox.relativeX = boundingBox.x;
          totalBoundingBox.relativeY = boundingBox.y;
        }
      }
      return elementToUse?.outerHTML?.replace(
        new RegExp(FORCE_FULL_WIDTH_HEIGHT_CLASS_ID, "g"),
        "",
      );
    });

    useExtractElementStore
      .getState()
      .setSelectedElementSnapshot(htmls.join("\n"), totalBoundingBox);
  }, [selectedNavTreeNode, multiselectedNavTreeNodes, storyboardId]);

  useEffect(() => {
    selectedElementKeyOnMouseUpRef.current = null;
  }, [selectedElementKey]);

  const handleMouseMoveWithDropPosition = useCallback(
    (event: DragEvent, fromMouseMove?: boolean) => {
      if (!fromMouseMove) {
        event.preventDefault();
        event.stopPropagation();
      }

      // On drag over, hover the bottom-most element
      const target = iframeDocRef.current?.elementFromPoint(
        event.offsetX,
        event.offsetY,
      ) as HTMLElement;
      if (!target) return;

      const selectedElement = TempoElement.fromKey(
        selectedElementKeyRef.current,
      );

      const tempDraggingStoryboard =
        useCanvasStore.getState().temporaryStoryboard;

      const draggingSourceElement = TempoElement.fromKey(
        tempDraggingStoryboard?._elementDragData?.selectedElementKey,
      );
      if (
        tempDraggingStoryboard &&
        tempDraggingStoryboard._elementDragData?.selectedElementKey
      ) {
        // Don't change the hovered element since this is just moving the element around absolutely
        // But set the hovered element to the parent of the source element
        if (
          useCanvasStore.getState().isDraggingAbsolute &&
          storyboardIdRef.current === draggingSourceElement.storyboardId &&
          tempDraggingStoryboard._elementDragData?.sourceParentElementKey
        ) {
          dispatch(
            setHoveredElementKey(
              tempDraggingStoryboard._elementDragData?.sourceParentElementKey,
            ),
          );
          return;
        }
      }

      const selectableData = getNavNodeToSelect(
        target,
        storyboardIdRef.current || "",
        selectedNavNodeRef.current,
        isSelectedTopLevelStoryboardRef.current,
        true,
      );

      if (selectableData?.navNode && isDrawingRef?.current) {
        updateDivSizeRef.current(event.offsetX, event.offsetY);
        return;
      }

      if (selectableData?.navNode) {
        // If dragging, do not hover over any child elements of the source element
        if (
          (!draggingSourceElement.isEmpty() &&
            draggingSourceElement.isParentOf(
              selectableData?.navNode?.tempoElement,
            )) ||
          draggingSourceElement.isEqual(selectableData?.navNode?.tempoElement)
        ) {
          return;
        }

        // If this element is a child of the selected element, just set the hovered element to the selected element
        if (
          selectableData.navNode.tempoElement.storyboardId ===
            selectedElement.storyboardId &&
          selectedElement.isStoryboard()
        ) {
          // Selected element is a storyboard - just set the hovered element to the selected element
          dispatch(setHoveredElementKey(selectedElementKeyRef.current));

          const storyboardNavTree =
            useCanvasStore.getState().storyboardToNavTree[
              selectedElement.storyboardId
            ];
          const navNodeToDropInto =
            selectedNavNodeRef.current ?? storyboardNavTree;

          // Calculate drop position for the top nav node in the storyboard
          if (!isNavTreeEmpty(navNodeToDropInto)) {
            calculateAndSetDropPosition(
              event.offsetX,
              event.offsetY,
              selectedNavNodeRef.current ?? storyboardNavTree,
              iframeDocRef.current as Document,
            );
          }
          return;
        }

        if (
          selectableData.navNode.tempoElement.storyboardId ===
            selectedElement.storyboardId &&
          selectedElement.isKnownElement() &&
          selectedElement.isParentOf(selectableData.navNode.tempoElement) &&
          selectedNavNodeRef.current
        ) {
          // Selected element is a parent of the hovered element - just set the hovered element to the selected element
          dispatch(setHoveredElementKey(selectedElementKeyRef.current));

          // Calculate drop position for the selected element
          calculateAndSetDropPosition(
            event.offsetX,
            event.offsetY,
            selectedNavNodeRef.current,
            iframeDocRef.current as Document,
          );
          return;
        }

        dispatch(
          setHoveredElementKey(selectableData.navNode.tempoElement.getKey()),
        );

        // Calculate drop position for the hovered element
        calculateAndSetDropPosition(
          event.offsetX,
          event.offsetY,
          selectableData.navNode,
          iframeDocRef.current as Document,
        );
      } else {
        // Hover the storyboard itself
        dispatch(
          setHoveredElementKey(
            TempoElement.forStoryboard(storyboardIdRef.current).getKey(),
          ),
        );
        useCanvasStore.getState().setDropPosition({
          position: null,
          dropCoordinatesInSb: {
            x: event.offsetX,
            y: event.offsetY,
          },
          dropCoordinatesForAbsolute: {
            x: event.offsetX,
            y: event.offsetY,
          },
        });
      }
    },
    [
      selectedElementKeyRef,
      storyboardIdRef,
      selectedNavNodeRef,
      isSelectedTopLevelStoryboardRef,
      isDrawingRef,
      dispatch,
      updateDivSizeRef,
    ],
  );
  const handleMouseMoveWithDropPositionRef = useAutoUpdateRef(
    handleMouseMoveWithDropPosition,
  );

  const handleMouseMove = useCallback(
    async (event: MouseEvent) => {
      // Missed the mouseup event, trigger it here
      if (isDraggingRef.current && event.buttons === 0) {
        dragOnMouseUp();
      }

      if (!allowInteractionsRef.current) {
        return;
      }

      if (isResizingRef.current) {
        return;
      }

      if (
        forceDragOverRef.current ||
        useCanvasStore.getState().isDragging ||
        cursorTypeRef.current === CursorType.Draw ||
        cursorTypeRef.current === CursorType.Text
      ) {
        return handleMouseMoveWithDropPositionRef.current(
          event as DragEvent,
          true,
        );
      }

      const target = iframeDocRef.current?.elementFromPoint(
        event.offsetX,
        event.offsetY,
      ) as HTMLElement;
      if (!target) return;

      const hoveredNavNodeData = getNavNodeToSelect(
        target,
        storyboardIdRef.current || "",
        selectedNavNodeRef.current,
        isSelectedTopLevelStoryboardRef.current,
        event.metaKey || event.ctrlKey,
      );

      // Handle element dragging
      const dragHandled = await dragOnMouseMove(
        event,
        selectedNavNodeRef.current,
        isDrawingRef?.current || false,
      );
      if (dragHandled) {
        return;
      }

      if (
        hoveredNavNodeData &&
        hoveredNavNodeData.navNode &&
        hoveredNavNodeData.navNode !== hoveredNavTreeNodeRef.current
      ) {
        const storyboardType =
          useCanvasStore.getState().storyboardIdToType[
            hoveredNavNodeData.navNode?.tempoElement.storyboardId
          ];
        if (
          !hoveredNavNodeData.navNode.parent &&
          storyboardType &&
          storyboardType !== STORYBOARD_TYPES.APPLICATION
        ) {
          // Select the storyboard itself
          dispatch(
            setHoveredElementKey(
              TempoElement.forStoryboard(
                hoveredNavNodeData.navNode.tempoElement.storyboardId,
              ).getKey(),
            ),
          );
          return;
        }

        dispatch(
          setHoveredElementKey(
            hoveredNavNodeData.navNode.tempoElement.getKey(),
          ),
        );
      } else if (!hoveredNavNodeData?.navNode) {
        dispatch(setHoveredElementKey(null));
      }
    },
    [
      isDraggingRef,
      allowInteractionsRef,
      isResizingRef,
      forceDragOverRef,
      storyboardIdRef,
      selectedNavNodeRef,
      isSelectedTopLevelStoryboardRef,
      cursorTypeRef,
      isDrawingRef,
      dragOnMouseMove,
      hoveredNavTreeNodeRef,
      dragOnMouseUp,
      handleMouseMoveWithDropPositionRef,
      dispatch,
    ],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleMouseMoveThrottled = useCallback(
    _.throttle(async (event: MouseEvent) => {
      await handleMouseMove(event);
    }, 10),
    [handleMouseMove],
  );

  const handleMouseDown = useCallback(
    (event: MouseEvent) => {
      selectedElementKeyOnMouseUpRef.current = null;

      if (!allowInteractionsRef.current) {
        return;
      }

      const target = iframeDocRef.current?.elementFromPoint(
        event.offsetX,
        event.offsetY,
      ) as HTMLElement;
      if (!target) return;

      const selectableData = getNavNodeToSelect(
        target,
        storyboardIdRef.current || "",
        selectedNavNodeRef.current,
        isSelectedTopLevelStoryboardRef.current,
        event.metaKey ||
          event.ctrlKey ||
          cursorTypeRef.current === CursorType.Draw ||
          cursorTypeRef.current === CursorType.Text,
      );

      if (!selectableData?.navNode) {
        return;
      }

      event.preventDefault();

      const isLeftClick = event.button === 0;

      if (cursorTypeRef.current === CursorType.Draw && isLeftClick) {
        startDrawDiv(
          selectableData?.navNode?.tempoElement.getKey(),
          event.offsetX,
          event.offsetY,
        );
        return;
      }

      if (cursorTypeRef.current === CursorType.Text && isLeftClick) {
        createTextDiv(
          selectableData?.navNode?.tempoElement.getKey(),
          event.offsetX,
          event.offsetY,
        );
        return;
      }

      let keyToSelect = selectableData.navNode.tempoElement.getKey();
      const storyboardType =
        useCanvasStore.getState().storyboardIdToType[
          selectableData.navNode.tempoElement.storyboardId
        ];
      if (
        !selectableData.navNode.parent &&
        storyboardType &&
        storyboardType !== STORYBOARD_TYPES.APPLICATION
      ) {
        keyToSelect = TempoElement.forStoryboard(
          selectableData.navNode.tempoElement.storyboardId,
        ).getKey();
      }

      const allSelectedNavNodes = multiselectedNavTreeNodesRef.current?.length
        ? multiselectedNavTreeNodesRef.current
        : [selectedNavNodeRef.current];
      const anyEqual = allSelectedNavNodes.some((node) => {
        return node === selectableData.navNode;
      });
      const anyParentOf = allSelectedNavNodes.some((node) => {
        return node?.tempoElement.isParentOf(
          selectableData.navNode.tempoElement,
        );
      });

      if (anyEqual || anyParentOf) {
        let selectedDomNode = selectableData.domElement;
        // In the case of the user selecting a child of the selected element, we still want to pass the
        // parent dom node to the drag on mouse down
        if (anyParentOf) {
          selectedDomNode = findElementByDomUniquePath(
            iframeDocRef.current as Document,
            allSelectedNavNodes[0]?.domUniquePath ??
              allSelectedNavNodes[0]?.domUniquePathForComponent,
          );
          selectedElementKeyOnMouseUpRef.current =
            selectableData.navNode.tempoElement.getKey();
        } else if (isLeftClick && anyEqual && !event.shiftKey) {
          // Clear the multiselected state on mouse up if the user is clicking a single element (but only left click)
          selectedElementKeyOnMouseUpRef.current = keyToSelect;
        }

        // Handle potential drag start
        dragOnMouseDown(event, selectedNavNodeRef.current, selectedDomNode);
        return;
      }

      // Handle potential drag start
      dragOnMouseDown(event, selectableData.navNode, selectableData.domElement);

      if (
        domUniquePathTextEditingRef.current &&
        domUniquePathTextEditingRef.current !==
          (selectableData.navNode.domUniquePath ??
            selectableData.navNode.domUniquePathForComponent)
      ) {
        handleBlurRef.current();
      }

      if (event.shiftKey) {
        dispatch(setMultiSelectWithElementKey(keyToSelect));
      } else {
        dispatch(setSelectedElementKey(keyToSelect));
      }
    },
    [
      allowInteractionsRef,
      storyboardIdRef,
      selectedNavNodeRef,
      isSelectedTopLevelStoryboardRef,
      cursorTypeRef,
      multiselectedNavTreeNodesRef,
      dragOnMouseDown,
      domUniquePathTextEditingRef,
      startDrawDiv,
      createTextDiv,
      dispatch,
      handleBlurRef,
    ],
  );

  useEffect(() => {
    if (!domUniquePathTextEditingRef.current) {
      return;
    }
    if (
      !selectedNavTreeNode ||
      selectedNavTreeNode.tempoElement.storyboardId !== storyboardId
    ) {
      handleBlurRef.current();
    }
  }, [
    selectedNavTreeNode,
    handleBlurRef,
    domUniquePathTextEditingRef,
    updateElementKeyBeingEdited,
    storyboardId,
  ]);

  const handleMouseUp = useCallback(
    (event: MouseEvent) => {
      if (!allowInteractionsRef.current) {
        return;
      }

      if (isDraggingRef.current) {
        // Handle element drag end
        dragOnMouseUp();
        return;
      }

      if (isDrawingRef.current) {
        finishDrawDiv();
        return;
      }

      if (selectedElementKeyOnMouseUpRef.current) {
        dispatch(setSelectedElementKey(selectedElementKeyOnMouseUpRef.current));
        selectedElementKeyOnMouseUpRef.current = null;
      }
    },
    [
      allowInteractionsRef,
      finishDrawDiv,
      isDrawingRef,
      dragOnMouseUp,
      dispatch,
      selectedElementKeyOnMouseUpRef,
      isDraggingRef,
    ],
  );

  const canEditTextRef = useAutoUpdateRef(canEditText);
  const startTextEditRef = useAutoUpdateRef(startTextEdit);

  const handleDoubleClick = useCallback(
    (event: MouseEvent) => {
      if (
        !allowInteractionsRef.current ||
        !selectedNavNodeRef.current ||
        !iframeDocRef.current
      ) {
        return;
      }
      const elementKey = selectedNavNodeRef.current.tempoElement.getKey();

      if (!canEditTextRef.current(elementKey)) {
        return;
      }

      // Find the actual DOM element in the iframe
      const domUniquePath =
        selectedNavNodeRef.current.domUniquePath ??
        selectedNavNodeRef.current.domUniquePathForComponent;
      const codebaseId = selectedNavNodeRef.current.tempoElement.codebaseId;

      // Search for the element with matching data attributes
      const elementToEdit = findElementByDomUniquePath(
        iframeDocRef.current,
        domUniquePath,
      );

      if (!elementToEdit) {
        return;
      }

      const codebaseIdFromElement = getCodebaseIdFromNode(elementToEdit);
      if (codebaseIdFromElement !== codebaseId) {
        return;
      }

      startTextEditRef.current(elementToEdit);

      event.preventDefault();
      event.stopPropagation();
    },
    [
      allowInteractionsRef,
      canEditTextRef,
      selectedNavNodeRef,
      startTextEditRef,
    ],
  );

  const { handleContextMenu } = useCanvasContextMenu(storyboardId);

  const setupElementSelection = useCallback(
    (iframeDoc: Document, eventHandler: HTMLDivElement) => {
      iframeDocRef.current = iframeDoc;

      eventHandler.addEventListener("mousemove", handleMouseMoveThrottled);
      eventHandler.addEventListener("mousedown", handleMouseDown);
      eventHandler.addEventListener("mouseup", handleMouseUp);
      eventHandler.addEventListener("dblclick", handleDoubleClick);
      eventHandler.addEventListener("contextmenu", handleContextMenu);

      const handleIframeKeyDown = (e: KeyboardEvent) => {
        if (domUniquePathTextEditingRef.current) {
          handleKeyDownRef.current(e);
        }
      };

      eventHandler.addEventListener(
        "dragover",
        handleMouseMoveWithDropPosition,
      );

      // These are directly on the iframe document because that's where the text is being edited
      iframeDoc.addEventListener("keydown", handleIframeKeyDown);

      return () => {
        eventHandler.removeEventListener("mousemove", handleMouseMoveThrottled);
        eventHandler.removeEventListener("mousedown", handleMouseDown);
        eventHandler.removeEventListener("mouseup", handleMouseUp);
        eventHandler.removeEventListener("dblclick", handleDoubleClick);
        eventHandler.removeEventListener("contextmenu", handleContextMenu);
        eventHandler.removeEventListener(
          "dragover",
          handleMouseMoveWithDropPosition,
        );

        iframeDoc.removeEventListener("keydown", handleIframeKeyDown);
      };
    },
    [
      domUniquePathTextEditingRef,
      handleContextMenu,
      handleDoubleClick,
      handleMouseMoveWithDropPosition,
      handleKeyDownRef,
      handleMouseDown,
      handleMouseMoveThrottled,
      handleMouseUp,
    ],
  );

  return {
    setupElementSelection,
    isEditingText: Boolean(domUniquePathTextEditing),
    isDragging,
    isDraggingAbsolute,
  };
};
